<template>
  <div class="device-extractions-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Device Extractions</h1>
          <p>Gestión de extracciones de datos de dispositivos</p>
        </v-col>
      </v-row>
      
      <!-- Filtros y estadísticas -->
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedDeviceSettingId"
            :items="deviceSettingOptions"
            label="Filtrar por Configuración"
            clearable
            @update:model-value="filterByDeviceSetting"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedType"
            :items="typeOptions"
            label="Filtrar por Tipo"
            clearable
            @update:model-value="filterByType"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Filtrar por Estado"
            clearable
            @update:model-value="filterByStatus"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-btn color="warning" block @click="showOverdue">
            <v-icon left>mdi-clock-alert</v-icon>
            Ver Atrasadas ({{ overdueCount }})
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="deviceExtractionsList"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Extracciones de Dispositivos</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Extracción
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.deviceSettingId="{ item }">
              <v-chip color="blue" variant="outlined" small>
                {{ item.deviceSettingId }}
              </v-chip>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip :color="getTypeColor(item.type)" small>
                <v-icon left size="small">{{ getTypeIcon(item.type) }}</v-icon>
                {{ item.type.toUpperCase() }}
              </v-chip>
            </template>
            <template v-slot:item.pollingInterval="{ item }">
              <v-chip color="indigo" variant="outlined" small>
                {{ formatInterval(item.pollingInterval) }}
              </v-chip>
            </template>
            <template v-slot:item.lastSyncAt="{ item }">
              <div class="d-flex align-center">
                <span>{{ formatDateTime(item.lastSyncAt) }}</span>
                <v-chip 
                  :color="item.isOverdue() ? 'error' : 'success'" 
                  size="x-small" 
                  class="ml-2"
                >
                  {{ item.getTimeSinceLastSync() }}
                </v-chip>
              </div>
            </template>
            <template v-slot:item.nextSync="{ item }">
              <span :class="{ 'text-error': item.isOverdue() }">
                {{ formatDateTime(item.getNextSyncTime()) }}
              </span>
            </template>
            <template v-slot:item.active="{ item }">
              <v-switch
                v-model="item.active"
                :loading="toggleLoading[item.id]"
                color="success"
                @change="toggleStatus(item)"
                hide-details
                density="compact"
              ></v-switch>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item)" small>
                <v-icon left size="small">{{ getStatusIcon(item) }}</v-icon>
                {{ getStatusText(item) }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn 
                size="small" 
                icon 
                variant="text" 
                @click="syncNow(item)"
                :loading="syncLoading[item.id]"
                :disabled="!item.active"
              >
                <v-icon>mdi-sync</v-icon>
                <v-tooltip activator="parent" location="top">
                  Sincronizar ahora
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="editItem(item)">
                <v-icon>mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">
                  Editar extracción
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
                <v-tooltip activator="parent" location="top">
                  Eliminar extracción
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from 'vue'
import { useDeviceExtractionStore } from './store'
import { DeviceExtraction } from './models/deviceExtraction'

const deviceExtractionStore = useDeviceExtractionStore()
const loading = ref(false)
const selectedDeviceSettingId = ref<string | null>(null)
const selectedType = ref<string | null>(null)
const statusFilter = ref<string | null>(null)
const syncLoading = reactive<Record<string, boolean>>({})
const toggleLoading = reactive<Record<string, boolean>>({})

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Config ID', key: 'deviceSettingId' },
  { title: 'Tipo', key: 'type' },
  { title: 'Setting ID', key: 'settingId' },
  { title: 'Intervalo', key: 'pollingInterval' },
  { title: 'Última Sync', key: 'lastSyncAt' },
  { title: 'Próxima Sync', key: 'nextSync' },
  { title: 'Activo', key: 'active' },
  { title: 'Estado', key: 'status' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

// Opciones para los filtros
const deviceSettingOptions = ref([
  { title: 'Config 1', value: 'config1' },
  { title: 'Config 2', value: 'config2' },
  { title: 'Config 3', value: 'config3' },
])

const typeOptions = ref([
  { title: 'FTP', value: 'ftp' },
  { title: 'API', value: 'api' },
  { title: 'Database', value: 'database' },
  { title: 'File', value: 'file' },
])

const statusOptions = ref([
  { title: 'Activo', value: 'active' },
  { title: 'Inactivo', value: 'inactive' },
  { title: 'Atrasado', value: 'overdue' },
  { title: 'Sincronizado', value: 'synced' },
])

const deviceExtractionsList = computed(() => {
  let list = deviceExtractionStore.deviceExtractions
  
  if (selectedDeviceSettingId.value) {
    list = list.filter(item => item.deviceSettingId === selectedDeviceSettingId.value)
  }
  
  if (selectedType.value) {
    list = list.filter(item => item.type === selectedType.value)
  }
  
  if (statusFilter.value) {
    switch (statusFilter.value) {
      case 'active':
        list = list.filter(item => item.active)
        break
      case 'inactive':
        list = list.filter(item => !item.active)
        break
      case 'overdue':
        list = list.filter(item => item.isOverdue())
        break
      case 'synced':
        list = list.filter(item => !item.isOverdue() && item.active)
        break
    }
  }
  
  return list
})

const overdueCount = computed(() => {
  return deviceExtractionStore.deviceExtractions.filter(item => item.isOverdue()).length
})

const formatInterval = (minutes: number) => {
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

const formatDateTime = (date: Date | string) => {
  return new Date(date).toLocaleString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    ftp: 'blue',
    api: 'green',
    database: 'purple',
    file: 'orange'
  }
  return colors[type] || 'grey'
}

const getTypeIcon = (type: string) => {
  const icons: Record<string, string> = {
    ftp: 'mdi-folder-network',
    api: 'mdi-api',
    database: 'mdi-database',
    file: 'mdi-file'
  }
  return icons[type] || 'mdi-help-circle'
}

const getStatusColor = (item: DeviceExtraction) => {
  if (!item.active) return 'grey'
  if (item.isOverdue()) return 'error'
  return 'success'
}

const getStatusIcon = (item: DeviceExtraction) => {
  if (!item.active) return 'mdi-pause'
  if (item.isOverdue()) return 'mdi-clock-alert'
  return 'mdi-check-circle'
}

const getStatusText = (item: DeviceExtraction) => {
  if (!item.active) return 'Inactivo'
  if (item.isOverdue()) return 'Atrasado'
  return 'Al día'
}

const refreshData = async () => {
  loading.value = true
  try {
    await deviceExtractionStore.getDeviceExtractions()
  } finally {
    loading.value = false
  }
}

const filterByDeviceSetting = async (deviceSettingId: string | null) => {
  if (deviceSettingId) {
    loading.value = true
    try {
      await deviceExtractionStore.getDeviceExtractionsByDeviceSettingId(deviceSettingId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByType = async (type: string | null) => {
  if (type) {
    loading.value = true
    try {
      await deviceExtractionStore.getDeviceExtractionsByType(type)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByStatus = () => {
  // El filtrado por estado se maneja en el computed
}

const showOverdue = async () => {
  loading.value = true
  try {
    await deviceExtractionStore.getOverdueDeviceExtractions()
  } finally {
    loading.value = false
  }
}

const addItem = () => {
  console.log('Add new device extraction')
}

const editItem = (item: DeviceExtraction) => {
  console.log('Edit item:', item)
}

const deleteItem = (item: DeviceExtraction) => {
  console.log('Delete item:', item)
}

const syncNow = async (item: DeviceExtraction) => {
  syncLoading[item.id] = true
  try {
    await deviceExtractionStore.syncDeviceExtraction(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error syncing extraction:', error)
  } finally {
    syncLoading[item.id] = false
  }
}

const toggleStatus = async (item: DeviceExtraction) => {
  toggleLoading[item.id] = true
  try {
    await deviceExtractionStore.toggleDeviceExtractionStatus(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error toggling status:', error)
  } finally {
    toggleLoading[item.id] = false
  }
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.device-extractions-view {
  height: 100%;
}

.text-error {
  color: rgb(var(--v-theme-error)) !important;
}
</style>
